<template>
  <div class="container">
    <h1>Select Your API Pack</h1>

    <div class="input-group">
      <label for="email">Email Address:</label>
      <input id="email" v-model="email" type="email" placeholder="Enter your email" required />

      <p v-if="email && !isEmailValid" style="color: red; font-size: 13px;">
        Please enter a valid email address.
      </p>

    </div>

    <div class="input-group">
      <label for="apiKey">API Key:</label>
      <input id="apiKey" v-model="apiKey" type="text" placeholder="Enter your API key" required />

      <p v-if="apiKey && !isApiKeyValid" style="color: red; font-size: 13px;">
        API key must be alphanumeric (letters and numbers only).
      </p>

    </div>

    <div class="grid">
      <div
        v-for="pack in packs"
        :key="pack.id"
        :class="['pack', { selected: selectedPack === pack.id }]"
        @click="selectPack(pack.id)"
      >
        <h3>{{ pack.label }}</h3>
        <p>{{ pack.description }}</p>
        <div class="price">
          <span v-if="pack.discounted"><s>${{ pack.originalPrice }}</s> <strong>${{ pack.price }}</strong></span>
          <span v-else><strong>${{ pack.price }}</strong></span>
        </div>
        <div class="badge" v-if="pack.badge">{{ pack.badge }}</div>
      </div>
    </div>

    <p class="note">
      Each pack is <b>valid for 12 months</b>. You can use the credits at your own pace within that period.
      Each API call is weighted reflecting the processing workload involved — check out 
      <a href="credits" target="_blank"> <b>API Credits Page</b></a> to learn how many credits each API call consumes.
    </p>

    <table class="comparison">
      <thead>
        <tr>
          <th>Pack</th>
          <th>Credits</th>
          <th>Price</th>
          <th>Cost per Call</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="pack in packs" :key="pack.id">
          <td>
            {{ pack.label }}
            <span class="compare-badge" v-if="pack.badge">{{ pack.badge }}</span>
          </td>
          <td>{{ pack.credits }}</td>
          <td>${{ pack.price }}</td>
          <td>${{ (pack.price / pack.credits).toFixed(4) }}</td>
        </tr>
      </tbody>
    </table>

    <div class="button-group">
      <button class="cta" @click="$router.push('/')">Home</button>
      <button class="cta" :disabled="!selectedPack || !email || !apiKey || !isEmailValid  || !isApiKeyValid" @click="submitSelection">Continue</button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import api from '@/services/api'

const email = ref(localStorage.getItem('email') || '')
const apiKey = ref(localStorage.getItem('apiKey') || '')
const selectedPack = ref(Number(localStorage.getItem('selectedPack')) || 3000)

const packs = [
  { id: 1000, label: '1,000 Credits', price: 10, credits: 1000, description: 'Kickoff Pack', badge: 'Starter Pack', discounted: false },
  { id: 3000, label: '3,000 Credits', price: 25, originalPrice: 30, credits: 3000, description: 'Trending pack', badge: 'Trending', discounted: true },
  { id: 5000, label: '5,000 Credits', price: 40, originalPrice: 50, credits: 5000, description: 'Popular pick', badge: 'Best Seller', discounted: true },
  { id: 10000, label: '10,000 Credits', price: 75, originalPrice: 100, credits: 10000, description: 'Get the best deal', badge: 'Best Value', discounted: true }
]

const isEmailValid = computed(() => {
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return pattern.test(email.value)
})

const isApiKeyValid = computed(() => {
  const pattern = /^[a-zA-Z0-9]+$/;
  return pattern.test(apiKey.value);
});


function selectPack(id) {
  selectedPack.value = id
}

async function submitSelection() {

  if (!isEmailValid.value) {
    alert("Please enter a valid email address.");
    return;
  }

  if (!email.value || !apiKey.value || !selectedPack.value) {
    alert("Please complete all required fields.");
    return;
  }

  console.log(`You selected the ${selectedPack.value} pack.\nEmail: ${email.value}\nAPI Key: ${apiKey.value}`);

  try {
    const response = await api.post(`/site/avac/api/purchase/api-integration-pack/stripe/order/checkout`, null, {
      params: {
        email: email.value,
        apiKey: apiKey.value,
        selectedPack: selectedPack.value
      }
    })

    if (response.data) {
      window.location.href = response.data // redirect to Stripe Checkout
    }

  } catch (error) {
    console.error("API error:", error)
    alert("Something went wrong. Please try again.")
  }  

}

watch(email, (val) => {
  localStorage.setItem('email', val)
})

watch(apiKey, (val) => {
  localStorage.setItem('apiKey', val)
})

watch(selectedPack, (val) => {
  localStorage.setItem('selectedPack', val.toString())
})

</script>

<style scoped>
.container {
  max-width: 960px;
  margin: 0 auto;
  padding: 30px 20px;
  background-color: #EDF6F9;
  border-radius: 10px;  
  background: #EDF6F9;
  border-radius: 10px;
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  margin-bottom: 30px;
  color: #222;
}

.input-group {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  max-width: 400px;
}

.input-group label {
  font-weight: 600;
  margin-bottom: 6px;
}

.input-group input {
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.pack {
  border: 2px solid #ccc;
  border-radius: 10px;
  padding: 20px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s ease;
}

.pack.selected {
  border-color: #1af721;
  background-color: #FFFF8A;
}

.pack h3 {
  margin-top: 0;
  font-size: 18px;
}

.pack .price {
  margin-top: 10px;
  font-size: 16px;
  color: #333;
}

.pack .price s {
  color: #999;
  margin-right: 5px;
}

.pack .price strong {
  color: #d35400;
}

.pack .badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #087F8C;
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

.note {
  font-size: 14px;
  color: #666;
  margin-bottom: 30px;
  max-width: 700px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
}

.note a {
  color: #087F8C;
  text-decoration: none;
}

.note a:hover {
  text-decoration: underline;
}

.comparison {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
  font-size: 14px;
}

.comparison th, .comparison td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
}

.compare-badge {
  display: inline-block;
  margin-left: 6px;
  background: #087F8C;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
}

.button-group {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
}

.cta {
  background-color: #087F8C;
  color: white;
  font-weight: bold;
  border: none;
  padding: 12px 28px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.cta:hover {
  background-color: #218838;
}

.cta:disabled {
  background-color: #ccc;
  color: #666;
  cursor: not-allowed;
}
</style>
